import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { BookOpen, ChevronDown, ChevronRight, Lock } from 'lucide-react'
import { useCapabilities } from '../../hooks/useCapabilities'
import { useOpenPageHelp } from '../../hooks/backend/discipleship'
import { Sheet, SheetContent, SheetTitle } from '../../lib/shadcn/sheet'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { ManualMarkdown } from './ManualMarkdown'
import { sectionHref } from '../../utils/manualMarkdown'

type Status = 'not_assigned' | 'due' | 'certified'
type HelpSection = { key: string; heading: string; part_number: number; part_title: string; note: string | null; body_md: string; status: Status }
type HelpSop = {
  key: string
  title: string
  part_number: number
  purpose: string | null
  trigger: string | null
  who: string | null
  where: string | null
  steps: { n: number; text: string }[]
  check: string | null
  note: string | null
  status: Status
}
type PageHelp = { ok: true; route: string; pattern: string | null; closed_book: boolean; sections: HelpSection[]; sops: HelpSop[] }

const STATUS_TEXT: Record<Status, string> = {
  certified: 'You are certified on this',
  due: 'Due for study',
  not_assigned: 'Not assigned to you',
}

function StatusChip({ status }: { status: Status }) {
  return (
    <span
      className={cn(
        'shrink-0 rounded-full border px-2 py-0.5 text-[10px]',
        status === 'certified' && 'border-emerald-300 text-emerald-800 dark:text-emerald-300',
        status === 'due' && 'border-[hsl(var(--brand))] text-[hsl(var(--brand))]',
        status === 'not_assigned' && 'border-border text-muted-foreground',
      )}
    >
      {STATUS_TEXT[status]}
    </span>
  )
}

function Expandable({ title, meta, status, children }: { title: string; meta: string; status: Status; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="rounded-md border border-border">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-start gap-2 p-2.5 text-left">
        {open ? <ChevronDown className="mt-0.5 h-4 w-4 shrink-0" /> : <ChevronRight className="mt-0.5 h-4 w-4 shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-[11px] text-muted-foreground">{meta}</span>
        </span>
        <StatusChip status={status} />
      </button>
      {open && <div className="border-t border-border p-3">{children}</div>}
    </li>
  )
}

/**
 * "How this page works": the manual sections and SOPs for the page you are on,
 * read from discipleship_page_section_link (migration 4602).
 *
 * IN THE TOP BAR, NOT BESIDE EACH PAGE'S CAPTION. Every page draws its own
 * caption, so there is no shared header to put it in, and adding it to sixty
 * pages one by one is sixty places to forget. The top bar is on every page.
 *
 * Closed book: while a person has a test in progress the panel says so and shows
 * nothing (phase 3 sets closed_book; the panel already honours it).
 */
export function PageHelpButton() {
  const { canView } = useCapabilities()
  const { pathname } = useLocation()
  const help = useOpenPageHelp<PageHelp>()
  const [open, setOpen] = useState(false)
  const trigger = help.trigger

  useEffect(() => {
    if (open) trigger({ route: pathname }).catch(() => undefined)
  }, [open, pathname, trigger])

  if (!canView) return null
  const d = help.data

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="How this page works"
        title="How this page works"
      >
        <BookOpen className="h-4 w-4 text-[hsl(var(--brand))]" />
        <span className="hidden xl:inline">How this page works</span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
          <div className="border-b border-border px-4 py-3 pr-10">
            <SheetTitle className="text-base">How this page works</SheetTitle>
            <p className="text-xs text-muted-foreground">From the Fleetly Platform Manual, for {pathname}</p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {help.loading && !d ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Reading the manual…
              </div>
            ) : help.error ? (
              <p className="text-sm text-destructive">{help.error}</p>
            ) : d?.closed_book ? (
              <div className="flex items-start gap-2 rounded-md border border-border p-3 text-sm">
                <Lock className="mt-0.5 h-4 w-4 shrink-0" />
                You have a test in progress. The manual is closed until you submit it.
              </div>
            ) : d && d.sections.length === 0 && d.sops.length === 0 ? (
              <div className="rounded-md border border-dashed border-border p-4 text-sm">
                <p className="font-medium">No manual section yet</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  The manual does not describe this page yet. It has been noted so the page can be written up.
                </p>
              </div>
            ) : d ? (
              <div className="space-y-5">
                {d.sections.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sections</h3>
                    <ul className="space-y-2">
                      {d.sections.map((s) => (
                        <Expandable key={s.key} title={s.heading} meta={s.part_number === 0 ? 'Introduction' : `Part ${s.part_number}`} status={s.status}>
                          {s.note && <p className="mb-2 text-xs text-muted-foreground">{s.note}</p>}
                          <ManualMarkdown body={s.body_md} compact />
                          <Link to={sectionHref(s.key)} onClick={() => setOpen(false)} className="mt-3 inline-block text-xs text-[hsl(var(--brand))] hover:underline">
                            Open in the manual
                          </Link>
                        </Expandable>
                      ))}
                    </ul>
                  </div>
                )}
                {d.sops.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Procedures that run from this page</h3>
                    <ul className="space-y-2">
                      {d.sops.map((o) => (
                        <Expandable key={o.key} title={`${o.key}: ${o.title}`} meta={o.who ? `Who: ${o.who}` : `Part ${o.part_number}`} status={o.status}>
                          <dl className="space-y-1 text-xs">
                            {o.purpose && (
                              <div>
                                <dt className="inline font-semibold">Purpose: </dt>
                                <dd className="inline">{o.purpose}</dd>
                              </div>
                            )}
                            {o.trigger && (
                              <div>
                                <dt className="inline font-semibold">Trigger: </dt>
                                <dd className="inline">{o.trigger}</dd>
                              </div>
                            )}
                            {o.where && (
                              <div>
                                <dt className="inline font-semibold">Where: </dt>
                                <dd className="inline">{o.where}</dd>
                              </div>
                            )}
                          </dl>
                          <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm">
                            {o.steps.map((st) => (
                              <li key={st.n}>{st.text}</li>
                            ))}
                          </ol>
                          {o.check && (
                            <p className="mt-2 text-xs">
                              <span className="font-semibold">Check: </span>
                              {o.check}
                            </p>
                          )}
                          <Link to={sectionHref(o.key)} onClick={() => setOpen(false)} className="mt-3 inline-block text-xs text-[hsl(var(--brand))] hover:underline">
                            Open in the manual
                          </Link>
                        </Expandable>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
